<template>
  <div class="pt-1" ref="editorRoot">
    <div v-if="showPreviewButton" class="mb-2">
      <button type="button" class="btn btn-outline-secondary btn-sm" style="margin-bottom: -6px" @click="abrirModalPreviewHtml = true">
        <i class="feather-eye"/> Visualizar Conteúdo
      </button>
    </div>
    <Teleport to="body">
      <ModalPreviewHtmlContent
        v-if="showPreviewButton"
        :abrir="abrirModalPreviewHtml"
        :content="modelValue"
        @fechar="abrirModalPreviewHtml = false"
      />
    </Teleport>
    <Vue3SummernoteEditor
      :model-value="modelValue"
      v-bind="$attrs"
      :id="id"
      :config="editorConfig"
      @update:model-value="onEditorModelUpdate"
    />
  </div>
</template>

<script>
import Vue3SummernoteEditor from 'vue3-summernote-editor';
import ModalPreviewHtmlContent from '@/components/Bitzar/ModalPreviewHtmlContent.vue';
import { getTextLength } from '@/utils/htmlTextLength';

/** Inserções acima deste limiar num único evento são tratadas como colagem (ex.: chip do teclado mobile). */
const BULK_INSERT_CHAR_THRESHOLD = 30;

/**
 * Componente wrapper do editor Summernote (vue3-summernote-editor).
 * Centralizado em @/components/Bitzar para permitir adicionar funções e configurações comuns depois.
 * Inclui botão "Visualizar conteúdo" e modal de preview do HTML.
 *
 * Uso: <SummernoteEditor v-model="conteudoHtml" id="meuEditor" />
 */
export default {
  name: 'SummernoteEditor',
  components: { Vue3SummernoteEditor, ModalPreviewHtmlContent },
  props: {
    modelValue: {
      type: String,
      default: '',
    },
    id: {
      type: String,
      default: undefined,
    },
    /** Exibe o botão "Visualizar Conteúdo" e o modal de preview. */
    showPreviewButton: {
      type: Boolean,
      default: false,
    },
    blockPaste: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue', 'paste-blocked'],
  data() {
    return {
      abrirModalPreviewHtml: false,
      pasteEvents: ['paste', 'drop', 'beforeinput'],
      contentSnapshot: '',
      isRevertingContent: false,
    };
  },
  watch: {
    modelValue(val) {
      if (this.isRevertingContent)
        return;

      this.syncContentSnapshot(val ?? '');
    },
  },
  created() {
    const config = window.SUMMERNOTE_DEFAULT_CONFIGS || {};
    window.SUMMERNOTE_DEFAULT_CONFIGS = { ...config, lang: this.summernoteLang };
    this.syncContentSnapshot(this.modelValue ?? '');
  },
  computed: {
    summernoteLang() {
      const locale = this.$i18n?.locale || 'pt-BR';
      const map = { 'pt-BR': 'pt-BR', 'es': 'es-ES', 'en': 'en-US' };
      return map[locale] ?? 'pt-BR';
    },
    editorConfig() {
      const global = window.SUMMERNOTE_DEFAULT_CONFIGS || {};
      const custom = this.$attrs.options || {};
      const globalCallbacks = global.callbacks || {};
      const customCallbacks = custom.callbacks || {};
      return {
        ...global,
        ...custom,
        disableDragAndDrop: true,
        lang: this.summernoteLang,
        callbacks: {
          ...globalCallbacks,
          ...customCallbacks,
          onInit: (...args) => {
            this.syncContentSnapshot(this.modelValue ?? '');
            this.disableSummernoteDropzone();
            customCallbacks.onInit?.(...args);
            globalCallbacks.onInit?.(...args);
          },
        },
      };
    },
  },
  mounted() {
    this.addPasteBlockListeners();
  },
  beforeUnmount() {
    this.removePasteBlockListeners();
  },
  methods: {
    syncContentSnapshot(html) {
      this.contentSnapshot = html ?? '';
    },
    onEditorModelUpdate(newHtml) {
      if (this.isRevertingContent) {
        this.$emit('update:modelValue', newHtml);
        return;
      }

      if (this.blockPaste && this.isBulkInsert(newHtml)) {
        this.isRevertingContent = true;
        this.$emit('paste-blocked');
        this.revertEditorContent(this.contentSnapshot);
        this.$emit('update:modelValue', this.contentSnapshot);
        this.$nextTick(() => {
          this.isRevertingContent = false;
        });
        return;
      }

      this.syncContentSnapshot(newHtml);
      this.$emit('update:modelValue', newHtml);
    },
    isBulkInsert(newHtml) {
      const delta = getTextLength(newHtml) - getTextLength(this.contentSnapshot);
      return delta > BULK_INSERT_CHAR_THRESHOLD;
    },
    revertEditorContent(html) {
      const $ = window.jQuery || window.$;
      const root = this.$refs.editorRoot;
      if (!$ || !root)
        return;

      $(root).find('.note-editor').prev().each((_, el) => {
        const $el = $(el);
        if ($el.data('summernote'))
          $el.summernote('code', html ?? '');
      });
    },
    disableSummernoteDropzone() {
      const $ = window.jQuery || window.$;
      const root = this.$refs.editorRoot;
      if (!$ || !root)
        return;

      $(root).find('.note-dropzone').remove();
    },
    addPasteBlockListeners() {
      this.pasteEvents.forEach((eventName) => {
        this.$refs.editorRoot?.addEventListener(eventName, this.handlePasteInput, true);
      });
    },
    removePasteBlockListeners() {
      this.pasteEvents.forEach((eventName) => {
        this.$refs.editorRoot?.removeEventListener(eventName, this.handlePasteInput, true);
      });
    },
    handlePasteInput(event) {
      if (!this.blockPaste)
        return;

      const blockedInputTypes = ['insertFromPaste', 'insertFromPasteAsQuotation', 'insertFromDrop'];
      const isPasteInput = event.type === 'beforeinput' && blockedInputTypes.includes(event.inputType);
      const isPasteEvent = ['paste', 'drop'].includes(event.type);

      if (!isPasteInput && !isPasteEvent)
        return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
      this.$emit('paste-blocked');
    },
  },
};
</script>
